// NotificationPanel — slide-in panel listing recent notifications.
import { useMemo, useState, useCallback, useEffect, type FC } from 'react';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { useAppSelector } from '~/hooks/useAppSelector';
import {
  selectNotifications,
  selectNotificationHasMore,
  selectNotificationLoadMoreStatus,
  selectDeletingIds,
  selectClearAllStatus,
  markAllReadThunk,
  fetchMoreNotificationsThunk,
  clearAllNotificationsThunk,
} from '../slices/notificationSlice';
import NotificationItem from './NotificationItem';
import type { Notification } from '../api';
import translations from '../translations/en.json';
import Button from '~/common/components/Button';
import ToastRegion from '~/common/components/ToastRegion';
import type { ToastItem } from '~/common/components/ToastRegion';

interface Props {
  onClose: () => void;
  onNavigate: (notification: Notification) => void;
}

function resolveBoardCardTagKey(notification: Notification): string | null {
  if (!notification.board_id || !notification.card_id) return null;
  return `board-card:${notification.board_id}:${notification.card_id}`;
}

function resolveContinuousStackKey(notification: Notification): string | null {
  return resolveBoardCardTagKey(notification);
}

function groupContinuousCardDiscussionNotifications(
  notifications: Notification[]
): Notification[][] {
  const groups: Notification[][] = [];

  for (const notification of notifications) {
    const lastGroup = groups.at(-1);
    if (lastGroup && lastGroup.length > 0) {
      const lastGroupFirst = lastGroup.at(0);
      if (!lastGroupFirst) {
        groups.push([notification]);
        continue;
      }

      const notificationKey = resolveContinuousStackKey(notification);
      const lastGroupKey = resolveContinuousStackKey(lastGroupFirst);
      const canJoinLastGroup =
        notificationKey != null && lastGroupKey != null && notificationKey === lastGroupKey;

      if (canJoinLastGroup) {
        lastGroup.push(notification);
        continue;
      }
    }

    groups.push([notification]);
  }

  return groups;
}

const NotificationPanel: FC<Props> = ({ onClose, onNavigate }) => {
  const dispatch = useAppDispatch();
  const notifications = useAppSelector(selectNotifications);
  const hasMore = useAppSelector(selectNotificationHasMore);
  const loadMoreStatus = useAppSelector(selectNotificationLoadMoreStatus);
  const deletingIds = useAppSelector(selectDeletingIds);
  const clearAllStatus = useAppSelector(selectClearAllStatus);
  const groupedNotifications = useMemo(
    () => groupContinuousCardDiscussionNotifications(notifications),
    [notifications]
  );
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  // [why] Track delete errors as inline state so they survive panel close/reopen.
  // A toast inside the panel disappears when the panel is dismissed.
  const [deleteErrorIds, setDeleteErrorIds] = useState<Set<string>>(new Set());

  // [why] Clean up stale delete error IDs when notifications are removed
  // from the list (successful delete, clear-all, or server-side removal).
  useEffect(() => {
    const currentIds = new Set(notifications.map((n) => n.id));
    setDeleteErrorIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const id of prev) {
        if (!currentIds.has(id)) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [notifications]);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const onDeleteError = useCallback((id: string, message: string) => {
    setToasts((prev) => [...prev, { id: crypto.randomUUID(), message, variant: 'error' }]);
    // [why] Track delete errors inline so they persist even if the panel
    // is closed and reopened — the toast alone disappears with the panel.
    setDeleteErrorIds((prev) => new Set(prev).add(id));
  }, []);

  const handleMarkAllRead = () => {
    void dispatch(markAllReadThunk());
  };

  const handleClearAll = () => {
    // [why] Reset error state when retrying so the error banner disappears.
    void dispatch(clearAllNotificationsThunk());
  };

  const handleLoadMore = () => {
    void dispatch(fetchMoreNotificationsThunk());
  };

  const handleNavigate = (notification: Notification) => {
    onClose();
    onNavigate(notification);
  };

  return (
    <div
      className="absolute right-0 top-12 w-[380px] max-h-[calc(100vh-3.5rem)] overflow-y-auto bg-bg-base border border-border rounded-xl shadow-2xl z-50"
      role="dialog"
      aria-label={translations['Notifications.title']}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border sticky top-0 bg-bg-base">
        <h2 className="text-sm font-semibold text-base">{translations['Notifications.title']}</h2>
        <div className="flex items-center gap-3">
          <Button
            variant="link"
            size="sm"
            onClick={handleClearAll}
            className="text-subtle hover:text-base"
          >
            {translations['Notifications.clearAll']}
          </Button>
          <Button
            variant="link"
            size="sm"
            onClick={handleMarkAllRead}
            className="text-indigo-400 hover:text-indigo-300"
          >
            {translations['Notifications.markAllRead']}
          </Button>
        </div>
      </div>

      {/* Clear-all error banner — shown inline so it survives panel close/reopen */}
      {clearAllStatus === 'error' && (
        <div className="mx-4 mt-3 flex items-center justify-between rounded-lg border border-danger/30 bg-danger/10 px-3 py-2">
          <p className="text-sm text-danger">{translations['Notifications.clearAllError']}</p>
          <Button
            variant="link"
            size="sm"
            onClick={handleClearAll}
            className="text-danger hover:text-danger/80 shrink-0"
          >
            {translations['Notifications.retry']}
          </Button>
        </div>
      )}

      {/* Content */}
      {notifications.length === 0 && clearAllStatus !== 'error' ? (
        <p className="px-4 py-6 text-sm text-muted text-center">
          {translations['Notifications.empty']}
        </p>
      ) : (
        <>
          <div className="py-1">
            {groupedNotifications.map((group) => {
              const firstNotification = group[0];
              if (!firstNotification) return null;

              return (
                <NotificationItem
                  key={firstNotification.id}
                  notification={firstNotification}
                  {...(group.length > 1 ? { stackedNotifications: group } : {})}
                  onNavigate={handleNavigate}
                  deletingIds={deletingIds}
                  deleteErrorIds={deleteErrorIds}
                  onDeleteError={onDeleteError}
                />
              );
            })}
          </div>

          {(hasMore || loadMoreStatus === 'error') && (
            <div className="px-4 py-3 text-center">
              {loadMoreStatus === 'error' ? (
                <div className="flex flex-col items-center gap-2">
                  <p className="text-sm text-danger">
                    {translations['Notifications.loadMoreError']}
                  </p>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={handleLoadMore}
                    className="text-indigo-400 hover:text-indigo-300"
                  >
                    {translations['Notifications.loadMore']}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="link"
                  size="sm"
                  onClick={handleLoadMore}
                  disabled={loadMoreStatus === 'loading'}
                  className="text-indigo-400 hover:text-indigo-300"
                >
                  {loadMoreStatus === 'loading'
                    ? translations['Notifications.loading']
                    : translations['Notifications.loadMore']}
                </Button>
              )}
            </div>
          )}
        </>
      )}

      <ToastRegion toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
};

export default NotificationPanel;
